import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import QCNote, { hasQcStatusNote } from "./qcNote";
import { Alert, Col, Button, Row, Card, CardBody, CardTitle } from "reactstrap";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import ToolkitProvider, {
  Search,
} from "react-bootstrap-table2-toolkit";
import { useFetch } from "../utils/fetchHook";
import { isDemoMode, isBrowserSessionDemo } from "../utils/demoConfig";
import {
  WL_DEMO_TRACK_AUDIO_PLACEHOLDER,
  WL_DEMO_TRACK_AUDIO_PLACEHOLDER_MIME,
} from "../utils/demoTrackAudioPlaceholder";
import { getDemoTrackBlob, isWlDemoBlobRef, wlDemoBlobStorageKeyFromRef } from "../utils/demoTrackBlobStore";
import {  useLocation } from "react-router-dom";
import { getCookie } from "../jwt/_helpers/cookie";
import TableHelper from "../components/tableHelper";

import TracksUpdate from "../views/tracks/tracksUpdate";
import contributorRolesData from "../data/mockData/contributorRolesData";
import { setReleaseTracks, upsertReleaseTrack } from "../redux/tracks/action";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";

const API_URL = process.env.REACT_APP_API_URL_BASE;
const X_API_KEY = process.env.REACT_APP_X_API_KEY;

function pauseOtherTrackPlayers(playId) {
  const trArray = document.querySelectorAll('[id^="track_play_"]');
  trArray.forEach(audio => {
    if (!audio.paused && audio.id !== `track_play_${playId}`) {
      audio.pause();
    }
  });
}

function TrackAudioCell({ cell, row }) {
  const [src, setSrc] = useState("");
  const [mime, setMime] = useState("audio/wav");

  useEffect(() => {
    let cancelled = false;
    let objectUrl;

    const applyResolved = (url, m) => {
      if (!cancelled) {
        setSrc(url);
        setMime(m);
      }
    };

    (async () => {
      let raw = cell && String(cell).trim() ? cell : row.resource;
      let type = cell && String(cell).trim() ? "audio/mpeg" : "audio/wav";

      if (!raw || !String(raw).trim()) {
        if (isDemoMode() || isBrowserSessionDemo()) {
          applyResolved(WL_DEMO_TRACK_AUDIO_PLACEHOLDER, WL_DEMO_TRACK_AUDIO_PLACEHOLDER_MIME);
        } else {
          applyResolved("", type);
        }
        return;
      }

      const rawStr = String(raw).trim();
      if (isWlDemoBlobRef(rawStr)) {
        const sk = wlDemoBlobStorageKeyFromRef(rawStr);
        try {
          const blob = await getDemoTrackBlob(sk);
          if (cancelled) {
            return;
          }
          if (blob) {
            objectUrl = URL.createObjectURL(blob);
            applyResolved(objectUrl, blob.type || "audio/wav");
            return;
          }
        } catch (e) {
          console.warn("[TrackAudioCell] blob load failed", e);
        }
        if (!cancelled && (isDemoMode() || isBrowserSessionDemo())) {
          applyResolved(WL_DEMO_TRACK_AUDIO_PLACEHOLDER, WL_DEMO_TRACK_AUDIO_PLACEHOLDER_MIME);
        }
        return;
      }

      applyResolved(rawStr, type);
    })();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [cell, row.resource]);

  const playId = row.id != null ? row.id : `o${row.order != null ? row.order : ""}`;

  if (!src) {
    return <span style={{ color: "#999", fontSize: "12px" }}>Loading…</span>;
  }

  return (
    <audio
      controls
      id={"track_play_" + playId}
      controlsList="nodownload"
      onPlay={() => pauseOtherTrackPlayers(playId)}
    >
      <source src={src} type={mime} />
      Your browser does not support the audio element.
    </audio>
  );
}

const pagination = paginationFactory({
  sizePerPageList: [
    { text: "10", value: 10 },
    { text: "25", value: 25 },
    { text: "50", value: 50 },
    { text: "100", value: 100 }
  ]
});

const TracksTable = () => {
  const dispatch = useDispatch();
  const { SearchBar, ClearSearchButton } = Search;
  const token = getCookie("token");
  const location = useLocation();
  const currentID = location.pathname.split("/")[2];
  const tracksByRelease = useSelector((state) => state.tracks?.byReleaseId || {});

  const [selectedTrackToEdit, setTrackToEdit] = useState(null);
  const [successPATCH, successPATCHset] = useState(false);

  const [forceUpdate, forceUpdateSET] = useState(0);;

  const [data, error, loading] = useFetch("GET",`releases/${currentID}/`,token,false,forceUpdate);
  const [dataGenres] = useFetch( "GET", `genres/`, token, false);
  const contributorRoles = contributorRolesData;
  const [shareholderRoles] = useFetch( "GET", `shareholder-roles/`, token, false);
  const reduxTracks = tracksByRelease[String(currentID)];
  const effectiveTracks = Array.isArray(reduxTracks)
    ? reduxTracks
    : (Array.isArray(data?.tracks) ? data.tracks : []);

  useEffect(() => {
    if (data?.id == null || !Array.isArray(data?.tracks)) {
      return;
    }
    const key = String(data.id);
    const localTracks = tracksByRelease[key];
    if (!Array.isArray(localTracks) || localTracks.length === 0) {
      dispatch(setReleaseTracks({ releaseId: data.id, tracks: data.tracks }));
      return;
    }
    const mergedTracks = data.tracks.map(apiTrack => {
      const localTrack = localTracks.find(t => String(t.id) === String(apiTrack.id));
      return localTrack ? { ...apiTrack, ...localTrack } : apiTrack;
    });
    dispatch(setReleaseTracks({ releaseId: data.id, tracks: mergedTracks }));
  }, [data?.id, data?.tracks, dispatch]);

  const memoizedReleaseData = useMemo(() => {
    if (!data) return null;
    return { ...data, tracks: effectiveTracks };
  }, [data, effectiveTracks]);

  const memoizedDataProps = useMemo(() => {
    return {
      dataGenres,
      contributorRoles,
      shareholderRoles
    };
  }, [dataGenres, contributorRoles, shareholderRoles]);

  function rowHighlightStyle(row, rowIndex) {
    if (hasQcStatusNote(row)) {
      return "qc-issues-alert";
    }
    return "";
  }

  const setSuccessEdit = () => {
    successPATCHset(true);
    setTimeout(() => {
      successPATCHset(false);
    }, 3000);
  };

  const updateState = {
    forceUpdate,
    forceUpdateSET
  };
  const onEditClick = row => {
    setTrackToEdit(row);
    [].forEach.call(document.querySelectorAll("tbody tr"), function(el) {
      el.classList.remove("activeRow");
    });
    document
      .querySelector(`#rowEdit_${row.id}`)
      .closest("tr")
      .classList.add("activeRow");
  };

  const actionFormatter = (cell, row) => {
    return (
      <Button
        outline
        color="primary"
        id={`rowEdit_${row.id}`}
        onClick={() => onEditClick(row)}
      >
        Edit
      </Button>
    );
  };
  const mixFormatter = (cell, row) => (cell ? cell : "-");
  const nameFormatter = (cell, row) => <span className="fontWeight700">{cell}</span>;

  const lenghtFormatter = (cell, row) => {
    if (!cell) {
      return '';
    }
    let base = cell;
    let splited = base.split(":");
    splited[splited.length -1]  = splited[splited.length -1 ].substring(0,2)
    const newCell = splited.join(":");
    return newCell;
  };


  const trackCompleteFormatter = cell => {
    return (
      <>
        {cell ? (
          <Button className="btn-status" disabled color="success">
            Ready
          </Button>
        )
        :
          (
          <Button className="btn-status" disabled color="warning">
            Not Ready
          </Button>
        )}
      </>
    );
  };

  const trackAccStatusFormatter = cell => {
    return (
      <>
        {cell ? (
          <span className="p-4">
            <i className="fas fa-2x fa-check-circle text-success"></i>
          </span>
        )
        :
          (
            <span className="p-4">
              <i className="fas fa-2x fa-times-circle text-danger"></i>
            </span>
        )}
      </>
    );
  };

  const audioPlayerFormatter = (cell, row) => <TrackAudioCell cell={cell} row={row} />;

  const qcNoteFormatter = (cell, row, rowIndex) => {
    return (
      <QCNote {...row} noteType={"Track"} rowIndex={rowIndex}></QCNote>
    );
  };

  const columns = [
    {
      dataField: "name",
      text: "Name",
      sort: true,
      formatter: nameFormatter,
      headerStyle: (colum, colIndex) => {
        return { width: "190px" };
      }
    },
    {
      dataField: "mix_name",
      text: "Mix Name",
      sort: true,
      formatter: mixFormatter,
      headerStyle: (colum, colIndex) => {
        return { width: "135px" };
      }
    },
    {
      dataField: "track_lenght",
      text: "Mix Length",
      sort: true,
      formatter: lenghtFormatter,
      headerStyle: (colum, colIndex) => {
        return { width: "135px" };
      }
    },
    {
      dataField: "mp3_resource",
      text: "Player",
      sort: false,
      csvExport: false,
      formatter: audioPlayerFormatter,
      headerStyle: (colum, colIndex) => {
        return { width: "333px" };
      }
    },
    {
      dataField: "track_data_complete",
      text: "Status",
      sort: true,
      csvExport: false,
      formatter: trackCompleteFormatter,
      headerStyle: (colum, colIndex) => {
        return { width: "120px" };
      }
    },
    {
      dataField: "track_ready_for_accounting",
      text: "Acc Status",
      sort: true,
      csvExport: false,
      formatter: trackAccStatusFormatter,
      headerStyle: (colum, colIndex) => {
        return { width: "135px" };
      }
    },
    {
      dataField: "qc",
      text: "QC",
      formatter: qcNoteFormatter,
      sort: false,
      csvExport: false,
      headerStyle: (colum, colIndex) => {
        return { width: "80px" };
      }
    },
    {
      dataField: "action",
      text: "Action",
      formatter: actionFormatter,
      sort: false,
      csvExport: false,
      headerStyle: (colum, colIndex) => {
        return { width: "111px" };
      }
    },
  ];

  const patchRelease = (ID) => {
    axios({
      method: "patch",
      mode: 'cors',
      url: `${API_URL}releases/${ID}/`,
      data: {
        status: "ready",
        all_tracks_ready: true,
      },
      headers: {
        Authorization: `Bearer ${token}`,
        "x-api-key": X_API_KEY,
        "Content-Type": "application/json"
      }
    }).then(function(response) {
      if (response.status === 200) {
        forceUpdateSET(forceUpdate + 1);
      }
    });
  };

  useEffect(() => {
    if (!data?.id || data.status !== "offline") {
      return;
    }
    const allReady =
      effectiveTracks.length > 0 &&
      effectiveTracks.every(track => !!track.track_data_complete);
    if (allReady) {
      patchRelease(data.id);
    }
  }, [data?.id, data?.status, effectiveTracks]);

  return (
    <>
      <Card>
        <CardBody>
          <Row>
            <Col>
              <CardTitle tag="h4">Edit Tracks</CardTitle>
              {loading ? (
                <TableHelper loading />
              ) : error ? (
                <TableHelper error />
              ) : (
                <ToolkitProvider
                  keyField={"order"}
                  data={effectiveTracks}
                  columns={columns}
                  search={{
                    searchFormatted: true
                  }}
                  exportCSV
                >
                  {props => (
                    <div className="tracksTable">
                      <Row>
                        <Col>
                          <div className="customSearch">
                            <SearchBar {...props.searchProps} />
                            <ClearSearchButton
                              className="customClear"
                              text="x"
                              {...props.searchProps}
                            />
                          </div>
                        </Col>
                      </Row>
                      <BootstrapTable
                        rowClasses={rowHighlightStyle}
                        {...props.baseProps}
                        bordered={ false }
                        pagination={pagination}
                      />
                    </div>
                  )}
                </ToolkitProvider>
              )}
            </Col>
          </Row>
        </CardBody>
      </Card>

      {selectedTrackToEdit && (
        <TracksUpdate
          releaseData={memoizedReleaseData}
          id={selectedTrackToEdit.id}
          initialTrackData={selectedTrackToEdit}
          data={memoizedDataProps}
          updateState={updateState}
          setTrackToEdit={() => setTrackToEdit()}
          setSuccessEdit={() => setSuccessEdit()}
          onTrackSaved={(track) => {
            dispatch(upsertReleaseTrack({ releaseId: currentID, track }));
          }}
        />
      )}

      {successPATCH && (
        <Alert style={{ paddingTop: 20 }} color="success">
          Track has been updated!
        </Alert>
      )}
    </>
  );
};

export default TracksTable;
